import { useMemo, useCallback } from "react";
import { useQuery } from "convex/react";
import { api } from "../convex/_generated/api";
import { modulesTypes } from "../convex/system/permissions";
import { ALWAYS_ENABLED_MODULES } from "../convex/system/permissions/constants";
import { userRoles } from "../convex/system/users";
import { useLicense } from "../lib/licensing/license-context";
import { isOffline } from "../lib/offline/is-offline";
import { readAppShellSnapshot } from "../lib/offline/app-shell-snapshot";

export type Action = "read" | "create" | "update" | "delete";
export type Module = typeof modulesTypes.type;
/** @deprecated use RoleType */
export type Role = typeof userRoles.type;
export type RoleType = Role;

export function usePermissions(module?: Module) {
  const userContext = useQuery(api.system.context.queries.checkUserContext);

  // Tier de licencia on-prem: módulos concedidos por el token verificado.
  // `null` en cloud (sin restricción). Ver lib/licensing/license-context.
  const { allowedModules, allowedTables } = useLicense();

  const permissions = useQuery(
    api.system.permissions.queries.getUserPermissions,
    userContext && !("error" in userContext) && userContext !== null
      ? { module }
      : "skip",
  );

  // ─── Resiliencia offline (warm) ───────────────────────────────────────────
  // Si las queries no resolvieron y estamos sin conexión, caemos al snapshot
  // local del app-shell: el contexto y el set COMPLETO de permisos. Así `can()`
  // funciona para cualquier módulo aunque su query puntual nunca se haya pedido
  // antes de perder la red. Online se prioriza la query viva.
  const useSnapshot =
    isOffline() && (userContext === undefined || permissions === undefined);
  const snapshot = useMemo(
    () => (useSnapshot ? readAppShellSnapshot() : null),
    [useSnapshot],
  );
  const effectiveUserContext =
    userContext ?? (useSnapshot ? snapshot?.context : undefined);
  const effectivePermissions =
    permissions ?? (useSnapshot ? snapshot?.permissions : undefined);

  // ─── Derived context flags (memoized) ────────────────────────────────────
  const { isValidContext, isSuperAdmin, hasRole, userRole } = useMemo(() => {
    if (!effectiveUserContext || "error" in effectiveUserContext) {
      return { isValidContext: false, isSuperAdmin: false, hasRole: false, userRole: undefined };
    }
    const role =
      "role" in effectiveUserContext ? effectiveUserContext.role : undefined;
    return {
      isValidContext: true,
      isSuperAdmin: !!effectiveUserContext.isSuperAdmin,
      hasRole: role !== undefined && role !== null,
      userRole: role ?? undefined,
    };
  }, [effectiveUserContext]);

  // ─── O(1) permission lookup map ──────────────────────────────────────────
  const permissionMap = useMemo(() => {
    if (!effectivePermissions) return null;
    return new Map(effectivePermissions.map((p) => [p.module, p]));
  }, [effectivePermissions]);

  // ─── Stable callbacks ─────────────────────────────────────────────────────
  const can = useCallback(
    (action: Action, targetModule?: Module): boolean => {
      if (!isValidContext) return false;

      const moduleToCheck = targetModule ?? module;

      // Tier de licencia on-prem: si el token concede un subconjunto de módulos
      // (`allowedModules`), todo lo que no esté ahí queda bloqueado, salvo los
      // módulos núcleo/infra (ALWAYS_ENABLED). Aplica a TODOS los roles,
      // incluido super_admin. En cloud `allowedModules` es null → no restringe.
      // Si el gating pregunta por tabla (poco común), también checamos
      // `allowedTables` para no bloquear cuando la tabla viene como sub-allow
      // de otro módulo (`readOnlyTables`).
      if (
        allowedModules &&
        moduleToCheck &&
        !allowedModules.has(moduleToCheck) &&
        !allowedTables?.has(moduleToCheck) &&
        !ALWAYS_ENABLED_MODULES.includes(moduleToCheck)
      ) {
        return false;
      }

      if (isSuperAdmin) return true;
      if (!hasRole) return false;
      if (!permissionMap) return false;

      if (!moduleToCheck) return false;

      const p = permissionMap.get(moduleToCheck);
      if (!p) return false;

      switch (action) {
        case "read":   return p.canRead;
        case "create": return p.canCreate;
        case "update": return p.canUpdate;
        case "delete": return p.canDelete;
        default:       return false;
      }
    },
    [isValidContext, isSuperAdmin, hasRole, permissionMap, module, allowedModules, allowedTables],
  );

  const canAny = useCallback(
    (actions: Action[], targetModule?: Module): boolean =>
      actions.some((action) => can(action, targetModule)),
    [can],
  );

  const canAll = useCallback(
    (actions: Action[], targetModule?: Module): boolean =>
      actions.every((action) => can(action, targetModule)),
    [can],
  );

  const getPermission = useCallback(
    (targetModule?: Module) => {
      if (!permissionMap) return null;
      const moduleToCheck = targetModule ?? module;
      if (!moduleToCheck) return null;
      return permissionMap.get(moduleToCheck) ?? null;
    },
    [permissionMap, module],
  );

  const getAllowedActions = useCallback(
    (targetModule?: Module): Action[] => {
      const p = getPermission(targetModule);
      if (!p) return [];
      const allowed: Action[] = [];
      if (p.canRead)   allowed.push("read");
      if (p.canCreate) allowed.push("create");
      if (p.canUpdate) allowed.push("update");
      if (p.canDelete) allowed.push("delete");
      return allowed;
    },
    [getPermission],
  );

  const noPermissions =
    effectivePermissions === undefined || effectiveUserContext === undefined;

  return {
    permissions: effectivePermissions,
    can,
    canAny,
    canAll,
    getPermission,
    getAllowedActions,
    isLoading: noPermissions,
    noPermissions,
    userContext: effectiveUserContext,
    hasRole,
    userRole,
    currentUser: effectiveUserContext,
  };
}
